/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Volume } from '../../types';
import { formatBytes } from '../../utils/format';
import { Button, StatusTag, formatINR } from '../console/ui';
import { CloseIc } from '../console/icons';

/** Quota in GB from whichever field the backend populated (max_quota is bytes). 0 = unlimited. */
export const volumeSizeGB = (v: Volume): number =>
  v.max_quota ? v.max_quota / (1024 * 1024 * 1024) : v.size_gb ?? v.quota_gb ?? 0;

export const formatVolumeSize = (v: Volume): string => {
  const gb = volumeSizeGB(v);
  return gb === 0 ? 'Unlimited' : `${gb.toFixed(0)} GB`;
};

/** Used bytes from the last TrueNAS sync, or undefined when the backend doesn't report it. */
export const volumeUsedBytes = (v: Volume): number | undefined =>
  typeof v.used_bytes === 'number' ? v.used_bytes : typeof v.dataset_size === 'number' ? v.dataset_size : undefined;

/** "12.4 GB of 500 GB" / "12.4 GB" (unlimited) / undefined when usage is unknown. */
export const formatVolumeUsage = (v: Volume): string | undefined => {
  const used = volumeUsedBytes(v);
  if (used === undefined) return undefined;
  const gb = volumeSizeGB(v);
  return gb > 0 ? `${formatBytes(used, 1)} of ${gb.toFixed(0)} GB` : formatBytes(used, 1);
};

/** Pods mounting this volume, or undefined when the backend predates attached_to. */
export const volumeAttachments = (v: Volume) => (Array.isArray(v.attached_to) ? v.attached_to : undefined);

export const formatVolumeCost = (v: Volume): string | undefined =>
  typeof v.cost_this_month === 'number' ? formatINR(v.cost_this_month, v.cost_currency || 'INR') : undefined;

export const volumeServer = (v: Volume) => v.nfs_server || v.nfs_server_ip || '';
export const volumePath = (v: Volume) => v.nfs_path || v.nfs_export_path || '';

interface Props {
  volume: Volume | null;
  projectName?: string;
  onClose: () => void;
  onDelete: (v: Volume) => void;
}

/** Right-hand side panel opened from "Mount info" on the NFS volumes list. */
const VolumeMountPanel: React.FC<Props> = ({ volume, projectName, onClose, onDelete }) => {
  useEffect(() => {
    if (!volume) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [volume, onClose]);

  if (!volume) return null;

  const server = volumeServer(volume);
  const usage = formatVolumeUsage(volume);
  const attachments = volumeAttachments(volume);
  const cost = formatVolumeCost(volume);
  const path = volumePath(volume);
  const target = volume.mount_path || `/mnt/${volume.name}`;
  const mountCmd = server && path ? `sudo mount -t nfs ${server}:${path} ${target}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mountCmd);
      toast.success('Mount command copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const field = (label: string, value: React.ReactNode, mono?: boolean) => (
    <div className="flex flex-col gap-0.5">
      <div className="text-xs text-ps-subtle">{label}</div>
      <div className={`text-sm text-ps-text ${mono ? 'font-mono break-all' : ''}`}>{value}</div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="mount-title"
        className="relative h-full w-full max-w-[440px] bg-ps-card border-l border-ps-border p-6 flex flex-col gap-5 overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="ps-eyebrow">Mount info</span>
            <h2 id="mount-title" className="m-0 text-lg font-medium truncate">
              {volume.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover"
          >
            <CloseIc className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {field('Size', formatVolumeSize(volume))}
          {field('Status', <StatusTag status={volume.status} />)}
          {usage !== undefined && field('Used', usage)}
          {cost !== undefined && field('Cost this month', cost)}
          {field('Project', projectName || '-')}
          {field('Created', volume.created_at ? format(new Date(volume.created_at), 'd MMM yyyy') : '-')}
        </div>

        {attachments !== undefined &&
          field(
            'Attached to',
            attachments.length === 0 ? (
              <span className="text-ps-muted">Not attached to any pod</span>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-1.5">
                {attachments.map((a) => (
                  <li key={a.pod_id} className="flex items-center justify-between gap-3">
                    <Link to={`/pods/${a.pod_id}`} className="ps-link truncate">
                      {a.pod_name || a.pod_id.slice(0, 8)}
                    </Link>
                    <StatusTag status={a.status} />
                  </li>
                ))}
              </ul>
            ),
          )}

        <div className="flex flex-col gap-4 pt-4 border-t border-ps-raised">
          {field('NFS server', server || 'Not provisioned yet', !!server)}
          {field('Path', path || '-', !!path)}
          {volume.mount_path && field('Default mount path', volume.mount_path, true)}
          {field('Billing', <span className="capitalize">{volume.billing_period || '-'} · quota-based</span>)}
        </div>

        {mountCmd && (
          <pre className="m-0 p-3 rounded-lg bg-ps-chrome border border-ps-border text-xs text-ps-text whitespace-pre-wrap break-all">
            {mountCmd}
          </pre>
        )}
        <p className="m-0 text-xs text-ps-subtle">
          NFS volumes mount only into QuickPods GPU Pods. Pick this volume under storage when you launch a pod.
        </p>

        <div className="flex flex-wrap gap-2.5">
          {mountCmd && (
            <Button variant="secondary" onClick={copy}>
              Copy mount command
            </Button>
          )}
          <Link
            to="/pods/create"
            state={{ preselectedVolumeId: volume.id, projectId: volume.project_id }}
            className="ps-btn2"
          >
            Attach to new pod
          </Link>
          <Button
            variant="danger"
            onClick={() => onDelete(volume)}
            disabled={volume.status === 'pending_deletion' || volume.status === 'deleted'}
          >
            Delete
          </Button>
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default VolumeMountPanel;
